import { FilaDesplegable } from '@/components/ui/fila-desplegable'
// The errand's plan, as the Brain marks it (plan brain-run-bucle, 4.1): one
// line folded -«Step 2 of 5 · what it is doing»- with a thin progress rail,
// and the whole list unfolded. Ported from the Brain prototype
// (`dont-merge-front-vision`), where it was drawn and never fed; now it reads
// the run's `plan`.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, ChevronDown, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { planProgress, type PlanStep } from '@/services/brainApi'

export function PlanBar({ steps }: { steps: PlanStep[] }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const progress = planProgress(steps)
  if (!progress) return null

  const doneCount = steps.filter((s) => s.status === 'done').length
  const allDone = doneCount === steps.length
  const current = steps[progress.n - 1]
  const pct = Math.round((doneCount / steps.length) * 100)

  return (
    <div className="mb-2 overflow-hidden rounded-md bg-muted/30">
      <FilaDesplegable
        abierta={open}
        onAlternar={() => setOpen((v) => !v)}
        className="px-3"
        cabecera={<>
          {allDone
            ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
            : <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />}
          <span className="shrink-0 text-xs font-semibold tabular-nums text-muted-foreground">
            {t('brain.plan.paso', { n: progress.n, total: progress.total })}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="truncate text-xs font-medium text-foreground/85">{current.title}</span>
          <ChevronDown className={cn('ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
        </>}
      >
        <ol aria-label={t('brain.plan.titulo')} className="space-y-2 px-3 pb-3 pt-1">
          {steps.map((step, i) => (
            <li key={`${i}-${step.title}`} className="flex items-start gap-2 text-xs">
              <span className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded-full font-bold tabular-nums',
                step.status === 'done' ? 'bg-success/15 text-success' :
                step.status === 'active' ? 'bg-primary text-primary-foreground' :
                'bg-muted text-muted-foreground',
              )}>
                {step.status === 'done' ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={cn('pt-0.5', step.status === 'active' ? 'font-semibold text-foreground' : 'text-muted-foreground')}>
                {step.title}
              </span>
            </li>
          ))}
        </ol>
      </FilaDesplegable>

      {/* The thin rail shows how far it is while folded. */}
      {!open && (
        <div className="h-0.5 w-full bg-muted/40">
          <div
            className={cn('h-full transition-all duration-500', allDone ? 'bg-success' : 'bg-primary')}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  )
}
